"use client";

import { HOMEPAGE_EXPERIMENT_KEY } from "@reflect/domain";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { fireLeadConversion } from "../../lib/adPixels/conversions";
import { getAnalytics } from "../../lib/analytics";
import { offersLogIn, useSignedInBefore } from "../../lib/auth/signedInBefore";
import { webStorage } from "../../lib/browserStorage";
import { deviceProps } from "../../lib/media/device";
import { getMarketingSubject } from "../../lib/marketing/subject";
import { useTRPC } from "../../lib/trpc/context";
import { useAuth } from "../../lib/trpc/provider";

import { HERO_LOG_IN_CTA } from "./ctaVariants";
import { VARIANT_PAGES } from "./variants/registry";
import {
  parseVariantOverride,
  resolveHomepageVariant,
  resolveHomepageVariantSource,
  type HomepageVariant,
} from "./variantResolver";

/**
 * Claim this browser session's one `hp_experiment_viewed` for an experiment,
 * under legacy's session key (`Waitlist/utils/experiment.js` `getExposureKey`).
 * Without session storage every load counts as a new exposure, as in legacy.
 *
 * @returns `true` the first time in the session, `false` after.
 */
function claimExposure(experimentId: string): boolean {
  const key = `hp_experiment_viewed_${experimentId}`;
  try {
    const session = webStorage("session");
    if (session?.getItem(key)) return false;
    session?.setItem(key, "1");
  } catch {
    // A storage that throws is one that cannot remember; count the exposure.
  }
  return true;
}

/**
 * Send this load's `hp_experiment_viewed`, unless the session has already
 * counted one: the arm plus the stored first-touch attribution, with this
 * load's arm winning (legacy's `getExperimentProperties()`).
 *
 * Always as a beacon, which goes out at once and survives the page closing.
 * The SDK's batch would hold the funnel's first stage for up to a second, and
 * a visitor about to leave may not give it that (card #1122).
 *
 * @param variant - the arm on screen.
 * @param variantSource - how it was resolved, or why it was not.
 */
function trackView(variant: HomepageVariant, variantSource: string): void {
  getAnalytics()
    .then((analytics) => {
      if (!claimExposure(HOMEPAGE_EXPERIMENT_KEY)) return;
      analytics.track(
        "hp_experiment_viewed",
        {
          ...analytics.storedAttribution(),
          experiment_id: HOMEPAGE_EXPERIMENT_KEY,
          hp_variant: variant,
          variant_source: variantSource,
        },
        { beacon: true },
      );
    })
    .catch(() => undefined);
}

/**
 * Where a landing CTA goes. A visitor with a session goes straight to
 * recording. Without one, every CTA registers first, as legacy's do, except
 * the hero's Log in on a device somebody has signed in on (#1066).
 *
 * @param ctaVariant - the CTA's `cta_variant`.
 * @param signedIn - whether the visitor already has a session.
 * @returns the path to navigate to.
 */
function destinationOf(ctaVariant: string, signedIn: boolean): string {
  if (signedIn) return "/app/record";
  return ctaVariant === HERO_LOG_IN_CTA ? "/sign-in" : "/sign-up";
}

/** Legacy-compatible marketing homepage, serving the ported variant A. */
export function LegacyHomepage(): React.JSX.Element {
  const trpc = useTRPC();
  const { snapshot } = useAuth();
  const signedInBefore = useSignedInBefore();
  // `null` once the browser has no storage to keep a subject in, so there is
  // no assignment to ask for.
  const [subject, setSubject] = useState<string | null>();
  const [override, setOverride] = useState<HomepageVariant>();
  // One view per load, whichever trigger comes first. The session claim alone
  // cannot stop a second where session storage throws.
  const viewTracked = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- browser storage and the query string exist only after hydration of a static page
    setSubject(getMarketingSubject() ?? null);
    const forced = parseVariantOverride(window.location.search);
    if (forced) setOverride(forced);
    getAnalytics()
      .then((analytics) => analytics.captureFirstTouchUtm(window.location.search))
      .catch(() => undefined);
  }, []);

  const assignment = useQuery({
    ...trpc.feedback.assignment.queryOptions({
      subject: subject ?? "pending-subject",
      ...(override ? { variantOverride: override } : {}),
    }),
    enabled: typeof subject === "string",
  });
  const assigned = assignment.data?.variant;
  const variant = resolveHomepageVariant(override, assigned);
  const variantSource = resolveHomepageVariantSource(override, assigned, assignment.data?.source);

  // Kept for signup attribution whenever the assignment lands, even after the
  // view has gone out without it.
  useEffect(() => {
    if (!assignment.data) return;
    getAnalytics()
      .then((analytics) =>
        analytics.captureHomepageAssignment(variant, variantSource, HOMEPAGE_EXPERIMENT_KEY),
      )
      .catch(() => undefined);
  }, [assignment.data, variant, variantSource]);

  const assignmentAnswered = assignment.data !== undefined || assignment.isError;
  // The view's `variant_source`: the resolver's once the assignment has
  // answered or an override made it moot; before that, why it has not —
  // legacy's `storage_unavailable` with no subject to ask with, else `pending`.
  const viewSource =
    assignmentAnswered || override !== undefined
      ? variantSource
      : subject === null
        ? "storage_unavailable"
        : "pending";

  /**
   * Count this load's view, once. Legacy counted every load on mount because
   * it resolved the arm in the browser; ours comes from the api, so the view
   * waits for it to answer, but never past the visitor leaving: a CTA click or
   * the page being hidden counts it with what is known then (#1090).
   */
  const recordView = useCallback((): void => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    trackView(variant, viewSource);
  }, [variant, viewSource]);

  const assignmentSettled = assignmentAnswered || subject === null;
  useEffect(() => {
    if (assignmentSettled) recordView();
  }, [assignmentSettled, recordView]);

  // Both, as Mixpanel's own unload flush listens to both: `visibilitychange` is
  // the one mobile browsers reliably fire, `pagehide` catches an unload that
  // skips it.
  useEffect(() => {
    function onLeave(event: Event): void {
      if (event.type === "pagehide" || document.visibilityState === "hidden") recordView();
    }
    document.addEventListener("visibilitychange", onLeave);
    window.addEventListener("pagehide", onLeave);
    return () => {
      document.removeEventListener("visibilitychange", onLeave);
      window.removeEventListener("pagehide", onLeave);
    };
  }, [recordView]);

  function onCta(ctaVariant: string): void {
    // Every visitor who clicks has seen the page.
    recordView();
    // Legacy fires the ad platforms' lead beside `landing_cta_clicked`, for
    // signed-in visitors too (`Waitlist/utils/handlers.js` `trackLandingCTAClick`).
    fireLeadConversion();
    getAnalytics()
      .then((analytics) =>
        analytics.track(
          "landing_cta_clicked",
          {
            cta_variant: ctaVariant,
            location: "landing_page",
            is_authenticated: snapshot.status === "authenticated",
            ...deviceProps(),
            ...analytics.storedAttribution(),
            hp_variant: variant,
            experiment_id: HOMEPAGE_EXPERIMENT_KEY,
          },
          { beacon: true },
        ),
      )
      .catch(() => undefined);
    window.location.href = destinationOf(ctaVariant, snapshot.status === "authenticated");
  }

  const VariantPage = VARIANT_PAGES[variant];

  return (
    // The page's one `<main>`, as legacy's landing page wraps everything below
    // the navbar in one. No arm renders a `main`, `header` or `footer`.
    <main data-test-id="home-root" data-variant={variant}>
      <VariantPage
        isAuthenticated={snapshot.status === "authenticated"}
        offersLogIn={offersLogIn(signedInBefore, snapshot.status)}
        onCTAClick={onCta}
      />
    </main>
  );
}
